import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { ExternalLink, GripVertical, Pause, Play, X } from 'lucide-react';
import { useAudio, useAudioTime } from '@/app/audio';
import { useContactDrawer } from '@/app/contact-drawer';
import { useChatClearance } from '@/app/support-chat';
import { formatClock } from '@/lib/format';
import { Button } from '@/components/ui/button';

const PLAYBACK_RATES = [0.5, 1, 1.25, 1.5, 2];

export function AudioControls({ showContactLink = true }: { showContactLink?: boolean }) {
  const audio = useAudio();
  const { current, duration } = useAudioTime();
  const drawer = useContactDrawer();
  if (!audio.track) return null;
  const nextRate = PLAYBACK_RATES[(PLAYBACK_RATES.indexOf(audio.rate) + 1) % PLAYBACK_RATES.length];
  return (
    <>
      <Button
        aria-label={audio.playing ? 'Pause recording' : 'Play recording'}
        size="icon-sm"
        onClick={audio.toggle}
      >
        {audio.playing ? <Pause /> : <Play />}
      </Button>
      <div className="audio-track">
        <div className="audio-track-heading">
          {showContactLink && audio.track.phone ? (
            <button
              className="audio-title"
              title="View contact"
              onClick={() => audio.track?.phone && drawer.open(audio.track.phone)}
            >
              {audio.track.title}
            </button>
          ) : (
            <strong className="audio-title">{audio.track.title}</strong>
          )}
          <span className="audio-time">
            {formatClock(current)} / {formatClock(duration)}
          </span>
        </div>
        <input
          className="audio-seek"
          style={{ backgroundImage: `linear-gradient(to right, var(--primary) ${duration > 0 ? Math.min(100, Math.max(0, (current / duration) * 100)) : 0}%, var(--border-strong) 0)` }}
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={Math.min(current, duration || 0)}
          onChange={(e) => audio.seek(Number(e.target.value))}
          aria-label="Recording position"
          aria-valuetext={`${formatClock(current)} of ${formatClock(duration)}`}
        />
      </div>
      <Button
        variant="ghost"
        size="xs"
        className="audio-speed"
        aria-label={`Playback speed ${audio.rate}x. Change to ${nextRate}x`}
        title={`Change speed to ${nextRate}x`}
        onClick={() => audio.setRate(nextRate)}
      >
        <span aria-live="polite">{audio.rate}x</span>
      </Button>
      {audio.playbackUrl ? <Button asChild variant="ghost" size="icon-sm">
        <a
          href={audio.playbackUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open recording in new tab"
          title="Open recording in new tab"
        >
          <ExternalLink />
        </a>
      </Button> : <Button variant="ghost" size="icon-sm" disabled aria-label="Open recording in new tab" title="Preparing recording…"><ExternalLink /></Button>}
      <Button variant="ghost" size="icon-sm" aria-label="Close recording player" onClick={audio.stop}>
        <X />
      </Button>
    </>
  );
}

export function AudioBar() {
  const { track } = useAudio();
  return track ? <FloatingAudioBar /> : null;
}

interface Position {
  x: number;
  y: number;
}

function FloatingAudioBar() {
  const dock = useRef<HTMLDivElement>(null);
  const drag = useRef<{ pointerId: number; start: Position; origin: Position } | null>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const [dragging, setDragging] = useState(false);
  const clearChat = useChatClearance(position);
  const dockRef = useCallback(
    (element: HTMLDivElement | null) => {
      dock.current = element;
      const release = clearChat(element);
      return () => {
        dock.current = null;
        release?.();
      };
    },
    [clearChat],
  );

  function clamp(next: Position): Position {
    const rect = dock.current?.getBoundingClientRect();
    return {
      x: Math.max(10, Math.min(next.x, window.innerWidth - (rect?.width || 0) - 10)),
      y: Math.max(10, Math.min(next.y, window.innerHeight - (rect?.height || 0) - 10)),
    };
  }

  useEffect(() => {
    const keepInView = () => setPosition((previous) => (previous ? clamp(previous) : null));
    window.addEventListener('resize', keepInView);
    const observer = new ResizeObserver(keepInView);
    if (dock.current) observer.observe(dock.current);
    return () => {
      window.removeEventListener('resize', keepInView);
      observer.disconnect();
    };
  }, []);

  function startDrag(event: PointerEvent<HTMLButtonElement>) {
    if (event.button !== 0 || !event.isPrimary || !dock.current) return;
    const rect = dock.current.getBoundingClientRect();
    const origin = { x: rect.left, y: rect.top };
    drag.current = { pointerId: event.pointerId, start: { x: event.clientX, y: event.clientY }, origin };
    event.currentTarget.setPointerCapture(event.pointerId);
    setPosition(origin);
    setDragging(true);
  }

  function moveDrag(event: PointerEvent<HTMLButtonElement>) {
    const active = drag.current;
    if (!active || active.pointerId !== event.pointerId) return;
    setPosition(
      clamp({
        x: active.origin.x + event.clientX - active.start.x,
        y: active.origin.y + event.clientY - active.start.y,
      }),
    );
  }

  function endDrag() {
    drag.current = null;
    setDragging(false);
  }

  function moveWithKeyboard(event: KeyboardEvent<HTMLButtonElement>) {
    const offsets: Record<string, Position> = {
      ArrowLeft: { x: -1, y: 0 },
      ArrowRight: { x: 1, y: 0 },
      ArrowUp: { x: 0, y: -1 },
      ArrowDown: { x: 0, y: 1 },
    };
    const offset = offsets[event.key];
    if (!offset || !dock.current) return;
    event.preventDefault();
    const rect = dock.current.getBoundingClientRect();
    const step = event.shiftKey ? 40 : 10;
    setPosition(clamp({ x: rect.left + offset.x * step, y: rect.top + offset.y * step }));
  }

  return (
    <div
      ref={dockRef}
      className="audio-dock"
      role="region"
      aria-label="Recording player"
      data-dragging={dragging || undefined}
      style={position ? { left: position.x, top: position.y, bottom: 'auto', transform: 'none' } : undefined}
    >
      <button
        type="button"
        className="audio-drag-handle"
        aria-label="Move recording player"
        title="Drag this handle to move the player"
        aria-description="Use the keyboard arrow keys to move the player. Hold Shift for a larger step."
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
        onKeyDown={moveWithKeyboard}
      >
        <GripVertical size={16} />
      </button>
      <AudioControls />
    </div>
  );
}
